import { sanitizeFileName } from "utils/sanitizeFileName";
import type {
  CountListPositionRow,
  CountListUnmatchedSource,
} from "../buildCountListRows";
import type { GroupValuesOf } from "../buildExportGroupValues";
import type { BuildGaebX83 } from "./types";

const GAEB_NAMESPACE = "http://www.gaeb.de/GAEB_DA_XML/DA83/3.2";
const SHORT_TEXT_MAX_LENGTH = 70;
const SHORT_TEXT_TAIL_LENGTH = 24;
const MIN_BOILERPLATE_SUFFIX_LENGTH = 15;
const UNIT_MAX_LENGTH = 4;
const FALLBACK_LEVEL_WIDTHS = [2, 4];

const GAEB_UNIT_BY_ALIAS: Record<string, string> = {
  stück: "St",
  stueck: "St",
  stk: "St",
  st: "St",
  pcs: "St",
  piece: "St",
  pieces: "St",
  m: "m",
  meter: "m",
  meters: "m",
  lfm: "m",
  pauschal: "psch",
  psch: "psch",
};

const XML_ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
};

interface ExportItem {
  description: string;
  unit: string;
  quantity: number;
}

interface NumberedPosition extends ExportItem {
  segments: string[];
  sectionName: string;
}

interface CategoryNode {
  number: string;
  children: Map<string, CategoryNode>;
  items: { number: string; item: ExportItem }[];
  sectionNameCounts: Map<string, number>;
}

const escapeXml = (value: string): string =>
  value.replace(/[&<>"]/g, (character) => XML_ESCAPES[character]);

const normalizeWhitespace = (value: string): string =>
  value.replace(/\s+/g, " ").trim();

// A comma-suffix repeated across positions (e.g. ", Planung Elektro FA. ...") carries no distinguishing information.
const collectBoilerplateSuffixes = (descriptions: string[]): Set<string> => {
  const suffixCounts = new Map<string, number>();
  for (const description of descriptions) {
    // commaIndex 0 would make the whole description a strippable suffix and empty the short text.
    const commaIndex = description.lastIndexOf(", ");
    if (commaIndex <= 0) continue;
    const suffix = description.slice(commaIndex);
    if (suffix.length < MIN_BOILERPLATE_SUFFIX_LENGTH) continue;
    suffixCounts.set(suffix, (suffixCounts.get(suffix) ?? 0) + 1);
  }
  const repeated = [...suffixCounts]
    .filter(([, count]) => count >= 2)
    .map(([suffix]) => suffix);
  return new Set(repeated);
};

const shortenText = (
  value: string,
  boilerplateSuffixes: Set<string>,
): string => {
  let normalized = normalizeWhitespace(value);
  if (normalized.length <= SHORT_TEXT_MAX_LENGTH) return normalized;
  const boilerplate = [...boilerplateSuffixes].find((suffix) =>
    normalized.endsWith(suffix),
  );
  if (boilerplate) {
    normalized = normalized.slice(0, -boilerplate.length);
  }
  if (normalized.length <= SHORT_TEXT_MAX_LENGTH) return normalized;
  // Cut in the middle: descriptions often differ only at the end ("... nach oben" vs "... nach rechts").
  const tail = normalized.slice(-SHORT_TEXT_TAIL_LENGTH);
  const head = normalized.slice(
    0,
    SHORT_TEXT_MAX_LENGTH - 3 - SHORT_TEXT_TAIL_LENGTH,
  );
  return `${head}...${tail}`;
};

const mapGaebUnit = (unit: string): string => {
  const normalized = normalizeWhitespace(unit);
  const mapped = GAEB_UNIT_BY_ALIAS[normalized.toLowerCase()];
  if (mapped) return mapped;
  return normalized.slice(0, UNIT_MAX_LENGTH) || "St";
};

const formatGaebQuantity = (quantity: number): string =>
  String(Math.round(quantity * 1000) / 1000);

// An item has one unit, so the groups' unit is used only when every group on the position agrees
const positionUnitOf = (
  row: CountListPositionRow,
  groupValuesOf: GroupValuesOf,
): string => {
  let agreedUnit: string | null = null;
  for (const detectedType of row.detectedTypes) {
    const groupUnit = groupValuesOf({
      kind: detectedType.kind,
      name: detectedType.name,
    }).unit;
    if (agreedUnit === null) {
      agreedUnit = groupUnit;
      continue;
    }
    if (groupUnit !== agreedUnit) return row.unit;
  }
  return agreedUnit ?? row.unit;
};

const splitPositionNumber = (positionNumber: string): string[] => {
  const normalized = positionNumber.trim().replace(/\.+$/, "");
  if (!normalized) return [];
  return normalized.split(".").map((segment) => segment.trim());
};

const createCategoryNode = (number: string): CategoryNode => ({
  number,
  children: new Map(),
  items: [],
  sectionNameCounts: new Map(),
});

const countSectionName = (node: CategoryNode, sectionName: string): void => {
  if (!sectionName) return;
  node.sectionNameCounts.set(
    sectionName,
    (node.sectionNameCounts.get(sectionName) ?? 0) + 1,
  );
};

const dominantSectionName = (node: CategoryNode): string => {
  let dominantName = "";
  let dominantCount = 0;
  for (const [name, count] of node.sectionNameCounts) {
    if (count > dominantCount) {
      dominantName = name;
      dominantCount = count;
    }
  }
  return dominantName;
};

export const buildGaebX83: BuildGaebX83 = ({
  rows,
  projectName,
  labels,
  groupValuesOf,
}) => {
  const numberedPositions: NumberedPosition[] = [];
  const appendixBuckets: Record<
    CountListUnmatchedSource | "unassigned",
    ExportItem[]
  > = { element: [], route: [], diagram: [], unassigned: [] };
  const seenPositionNumbers = new Set<string>();
  let currentSectionName = "";

  for (const row of rows) {
    if (row.kind === "section") {
      currentSectionName = normalizeWhitespace(row.sectionName);
      continue;
    }
    // Diagram-only items are never counted on plans, so their quantity lives in diagramsValue.
    const quantity =
      row.countedTotal > 0 ? row.countedTotal : row.diagramsValue;
    if (quantity === 0) continue;
    if (row.kind === "unmatched") {
      appendixBuckets[row.source].push({
        description: row.name,
        unit:
          row.source === "diagram"
            ? row.unit
            : groupValuesOf({ kind: row.source, name: row.name }).unit,
        quantity,
      });
      continue;
    }
    const unit = positionUnitOf(row, groupValuesOf);
    const segments = splitPositionNumber(row.positionNumber);
    const isUsableNumber =
      segments.length >= 2 &&
      segments.every((segment) => /^\d+$/.test(segment));
    if (!isUsableNumber) {
      appendixBuckets.unassigned.push({
        description: row.description,
        unit,
        quantity,
      });
      continue;
    }
    // Numeric key so "1.2" and "01.2" dedupe as one position — padding would collide them into duplicate OZs.
    const positionKey = segments
      .map((segment) => String(parseInt(segment, 10)))
      .join(".");
    if (seenPositionNumbers.has(positionKey)) continue;
    seenPositionNumbers.add(positionKey);
    numberedPositions.push({
      segments,
      sectionName: currentSectionName,
      description: row.description,
      unit,
      quantity,
    });
  }

  const depthCounts = new Map<number, number>();
  for (const position of numberedPositions) {
    depthCounts.set(
      position.segments.length,
      (depthCounts.get(position.segments.length) ?? 0) + 1,
    );
  }
  let dominantDepth = 0;
  let dominantDepthCount = 0;
  for (const [depth, count] of depthCounts) {
    if (count > dominantDepthCount) {
      dominantDepth = depth;
      dominantDepthCount = count;
    }
  }

  // Positions outside the dominant numbering pattern cannot fit the OZ mask.
  const treePositions: NumberedPosition[] = [];
  for (const position of numberedPositions) {
    if (position.segments.length === dominantDepth) {
      treePositions.push(position);
    } else {
      appendixBuckets.unassigned.push(position);
    }
  }

  const appendixSections = [
    { label: labels.elementsSection, items: appendixBuckets.element },
    { label: labels.cablesSection, items: appendixBuckets.route },
    { label: labels.diagramsSection, items: appendixBuckets.diagram },
    { label: labels.unassignedSection, items: appendixBuckets.unassigned },
  ].filter((section) => section.items.length > 0);

  if (treePositions.length === 0 && appendixSections.length === 0) return null;

  const levelWidths =
    treePositions.length > 0
      ? Array.from({ length: dominantDepth }, (_, level) =>
          Math.max(
            ...treePositions.map((position) => position.segments[level].length),
          ),
        )
      : [...FALLBACK_LEVEL_WIDTHS];

  const maxTopNumber = treePositions.reduce(
    (max, position) => Math.max(max, parseInt(position.segments[0], 10)),
    0,
  );
  if (appendixSections.length > 0) {
    levelWidths[0] = Math.max(
      levelWidths[0],
      String(maxTopNumber + appendixSections.length).length,
    );
    const itemLevel = levelWidths.length - 1;
    const largestSectionSize = Math.max(
      ...appendixSections.map((section) => section.items.length),
    );
    levelWidths[itemLevel] = Math.max(
      levelWidths[itemLevel],
      String(largestSectionSize).length,
    );
  }

  // NOTE: GAEB mandates a fixed digit width per OZ level, so shorter segments are zero-padded (1.9.40 -> 1.09.040).
  const padSegment = (segment: string, level: number): string =>
    segment.padStart(levelWidths[level], "0");

  const root = createCategoryNode("");
  for (const position of treePositions) {
    let node = root;
    for (let level = 0; level < position.segments.length - 1; level += 1) {
      const categoryNumber = padSegment(position.segments[level], level);
      let child = node.children.get(categoryNumber);
      if (!child) {
        child = createCategoryNode(categoryNumber);
        node.children.set(categoryNumber, child);
      }
      countSectionName(child, position.sectionName);
      node = child;
    }
    node.items.push({
      number: padSegment(
        position.segments[position.segments.length - 1],
        position.segments.length - 1,
      ),
      item: position,
    });
  }

  appendixSections.forEach((section, sectionIndex) => {
    const sectionRoot = createCategoryNode(
      padSegment(String(maxTopNumber + 1 + sectionIndex), 0),
    );
    countSectionName(sectionRoot, section.label);
    let sectionLeaf = sectionRoot;
    for (let level = 1; level < levelWidths.length - 1; level += 1) {
      const filler = createCategoryNode(padSegment("1", level));
      countSectionName(filler, section.label);
      sectionLeaf.children.set(filler.number, filler);
      sectionLeaf = filler;
    }
    section.items.forEach((item, itemIndex) => {
      sectionLeaf.items.push({
        number: String(itemIndex + 1).padStart(
          levelWidths[levelWidths.length - 1],
          "0",
        ),
        item,
      });
    });
    root.children.set(sectionRoot.number, sectionRoot);
  });

  const boilerplateSuffixes = collectBoilerplateSuffixes(
    [
      ...treePositions,
      ...appendixSections.flatMap((section) => section.items),
    ].map((item) => normalizeWhitespace(item.description)),
  );

  const lines: string[] = [];
  const writeLine = (indent: number, text: string): void => {
    lines.push(`${"  ".repeat(indent)}${text}`);
  };
  const writeLeaf = (indent: number, tag: string, value: string): void => {
    writeLine(indent, `<${tag}>${escapeXml(value)}</${tag}>`);
  };

  const writeItem = (
    indent: number,
    ozPath: string,
    entry: { number: string; item: ExportItem },
  ): void => {
    writeLine(indent, `<Item ID="ID_${ozPath}" RNoPart="${entry.number}">`);
    // NOTE: X83 is the tender request and carries quantities only; prices belong to the X84 offer.
    writeLeaf(indent + 1, "Qty", formatGaebQuantity(entry.item.quantity));
    writeLeaf(indent + 1, "QU", mapGaebUnit(entry.item.unit));
    writeLine(indent + 1, "<Description>");
    writeLine(indent + 2, "<CompleteText>");
    writeLeaf(indent + 3, "ComplTSA", "No");
    writeLeaf(indent + 3, "ComplTSB", "No");
    writeLine(
      indent + 3,
      `<DetailTxt><Text><p><span>${escapeXml(normalizeWhitespace(entry.item.description))}</span></p></Text></DetailTxt>`,
    );
    writeLine(indent + 3, "<OutlineText>");
    writeLeaf(indent + 4, "OutlTSA", "No");
    writeLine(
      indent + 4,
      `<OutlTxt><TextOutlTxt><span>${escapeXml(shortenText(entry.item.description, boilerplateSuffixes))}</span></TextOutlTxt></OutlTxt>`,
    );
    writeLine(indent + 3, "</OutlineText>");
    writeLine(indent + 2, "</CompleteText>");
    writeLine(indent + 1, "</Description>");
    writeLine(indent, "</Item>");
  };

  const writeCategory = (
    indent: number,
    node: CategoryNode,
    parentPath: string,
  ): void => {
    const ozPath = parentPath ? `${parentPath}_${node.number}` : node.number;
    writeLine(indent, `<BoQCtgy ID="ID_C_${ozPath}" RNoPart="${node.number}">`);
    const label = dominantSectionName(node);
    if (label) {
      writeLine(indent + 1, `<LblTx><span>${escapeXml(label)}</span></LblTx>`);
    }
    writeLine(indent + 1, "<BoQBody>");
    const children = [...node.children.values()].sort((first, second) =>
      first.number.localeCompare(second.number),
    );
    for (const child of children) {
      writeCategory(indent + 2, child, ozPath);
    }
    if (node.items.length > 0) {
      writeLine(indent + 2, "<Itemlist>");
      const items = [...node.items].sort((first, second) =>
        first.number.localeCompare(second.number),
      );
      for (const entry of items) {
        writeItem(indent + 3, `${ozPath}_${entry.number}`, entry);
      }
      writeLine(indent + 2, "</Itemlist>");
    }
    writeLine(indent + 1, "</BoQBody>");
    writeLine(indent, "</BoQCtgy>");
  };

  const now = new Date();
  const date = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const time = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}`;
  const trimmedProjectName = normalizeWhitespace(projectName);

  writeLine(0, `<?xml version="1.0" encoding="UTF-8"?>`);
  writeLine(0, `<GAEB xmlns="${GAEB_NAMESPACE}">`);
  writeLine(1, "<GAEBInfo>");
  writeLeaf(2, "Version", "3.2");
  writeLeaf(2, "VersDate", "2013-10");
  writeLeaf(2, "Date", date);
  writeLeaf(2, "Time", time);
  writeLeaf(2, "ProgSystem", "Monco");
  writeLeaf(2, "ProgName", "Monco Smart Surveyor");
  writeLine(1, "</GAEBInfo>");
  writeLine(1, "<PrjInfo>");
  writeLeaf(2, "NamePrj", trimmedProjectName);
  writeLeaf(2, "LblPrj", trimmedProjectName);
  writeLeaf(2, "Cur", "EUR");
  writeLeaf(2, "CurLbl", "Euro");
  writeLine(1, "</PrjInfo>");
  writeLine(1, "<Award>");
  writeLeaf(2, "DP", "83");
  writeLine(2, "<AwardInfo>");
  writeLeaf(3, "Cur", "EUR");
  writeLeaf(3, "CurLbl", "Euro");
  writeLine(2, "</AwardInfo>");
  writeLine(2, "<OWN><Address><Name1>Monco</Name1></Address></OWN>");
  writeLine(2, `<BoQ ID="ID_BOQ">`);
  writeLine(3, "<BoQInfo>");
  writeLeaf(4, "Name", trimmedProjectName);
  writeLeaf(4, "LblBoQ", trimmedProjectName);
  writeLeaf(4, "Date", date);
  writeLeaf(4, "OutlCompl", "AllTxt");
  levelWidths.forEach((width, level) => {
    const levelType = level === levelWidths.length - 1 ? "Item" : "BoQLevel";
    writeLine(
      4,
      `<BoQBkdn><Type>${levelType}</Type><Length>${width}</Length><Num>Yes</Num></BoQBkdn>`,
    );
  });
  writeLine(3, "</BoQInfo>");
  writeLine(3, "<BoQBody>");
  const topCategories = [...root.children.values()].sort((first, second) =>
    first.number.localeCompare(second.number),
  );
  for (const category of topCategories) {
    writeCategory(4, category, "");
  }
  writeLine(3, "</BoQBody>");
  writeLine(2, "</BoQ>");
  writeLine(1, "</Award>");
  writeLine(0, "</GAEB>");

  const safeName = sanitizeFileName({
    rawName: trimmedProjectName,
    fallback: "leistungsverzeichnis",
  });

  const appendixItemCount = appendixSections.reduce(
    (total, section) => total + section.items.length,
    0,
  );

  return {
    xml: lines.join("\n"),
    fileName: `${safeName}.X83`,
    positionCount: treePositions.length + appendixItemCount,
  };
};
